import { useEffect, useRef } from 'react';
import { useWebSocket } from '@utils/hooks/useWebSocket';
import { useDetailedOrders, useGetOrdersWithStatus } from '../../pages/Orders/hooks/orders';
import { OrderDetail, OrderEvent } from '@interfaces/OrderTypes';
import toast from 'react-hot-toast';
import { useNotificationContext } from '@context/NotificationsContext';
import { DELIVERY_MAP, NOTIFICATION_NAMES_MAP, PRINT_AUTO_OPTIONS_MAP, SOCKET_EVENTS } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import {
  useElectronPrintSettingsFromStore,
  selectAutoPrintPrinters,
  shouldSkipAutoPrint,
} from '@store/actions/electronPrintSettings';
import { useElectron } from './useElectron';
import useModules from './useModules';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useNavigate } from 'react-router-dom';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { useAuth } from '@context/AuthContext';
import { shouldIgnoreIncomingOrder, shouldAutoPrintIncomingOrder } from '@utils/hooks/incomingOrderVisibility';
import { isEventForCurrentCommerce } from '@utils/hooks/eventCommerceScope';
import { useWebBlePrint } from './useWebBlePrint';
import { getWebBlePrintMode } from '@utils/print/webble/printMode';
import { appStore } from '@store/index';
import { scheduledOrdersSlice } from '@store/slices/scheduledOrders';
import { selectAutoPrintWebBlePrinters, webBleAutoPrintWanted } from '@store/slices/webBlePrinters.types';
import { useMultisucursalGate } from '@utils/hooks/useMultisucursalGate';
import { notifyNewOrder } from '@utils/notifications/orderDesktopNotify';
import { formatScheduledDateTime } from '@pages/Orders/utils/scheduledOrderUtils';
export const useIncomingOrder = () => {
  const { subscribeEvent, unsubscribeEvent } = useWebSocket();
  const { getOrder } = useDetailedOrders();
  const { addNotification, removeNotification } = useNotificationContext();
  const { handleAddOrder, handleUpdateStatus } = useGetOrdersWithStatus();
  const allPrintersSettings = useElectronPrintSettingsFromStore();
  const { hasModule } = useModules();
  const { planId, accountCommerceTokens } = useSubscription();
  const accountCommerceTokensRef = useRef(accountCommerceTokens);
  const { printOrder } = useElectron();
  const { printOrder: webBlePrintOrder } = useWebBlePrint();
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  const { refreshLogin } = useAuth();
  // Multisucursal: un usuario sin permiso NO debe ver/oír ni imprimir pedidos de OTRAS sucursales.
  // En multi-sucursal todos los pedidos —incluido el propio— llegan por el canal de cuenta
  // `panel_<accountId>`, así que el filtro correcto es por notificación, no por suscripción (gatear
  // la suscripción rompió el tiempo real → incidente #1004). El gate (root, o no-root con el permiso
  // PEDIDOS_MULTISUCURSAL) está centralizado en useMultisucursalGate y lo comparten el dropdown
  // (useCanSwitchBranch) y el session-guard (useBranchSessionGuard).
  const canSeeOtherBranchOrders = useMultisucursalGate();
  // Vía ref (igual que accountCommerceTokens) para que el callback del socket lea SIEMPRE el valor
  // más reciente y no quede atrapado en un closure obsoleto cuando los módulos/permiso cargan o
  // cambian sin remount (relevante al montar useSubscriptionSocket con refetch en vivo).
  const canSeeOtherBranchOrdersRef = useRef(canSeeOtherBranchOrders);
  // Pedidos ya auto-impresos (id → timestamp) para no imprimir dos veces si el socket entrega
  // el mismo NEW_ORDER más de una vez (reconexión / doble emisión). Ref para sobrevivir renders
  // sin re-suscribir el socket. La dedup por evento se evalúa una sola vez (ver shouldSkipAutoPrint).
  const autoPrintedOrdersRef = useRef<Map<string, number>>(new Map());

  // Keep the ref in sync so the socket callback always has the latest tokens
  useEffect(() => {
    accountCommerceTokensRef.current = accountCommerceTokens;
  }, [accountCommerceTokens]);

  useEffect(() => {
    canSeeOtherBranchOrdersRef.current = canSeeOtherBranchOrders;
  }, [canSeeOtherBranchOrders]);

  useEffect(() => {
    // Auto-print por impresora Web Bluetooth (BLE), espejo de la rama Electron. Aislado y
    // a prueba de fallos: NUNCA debe romper la recepción de pedidos. Lee estado FRESCO del
    // store y del master toggle. `webBlePrintOrder` maneja las copias internamente.
    const autoPrintWebBle = (order: OrderDetail, trigger: 'received' | 'accepted') => {
      try {
        if (!getWebBlePrintMode()) return;
        // FIX (HIGH, stale closure): leer los módulos FRESCOS del store en el momento del
        // evento de socket, NO el `hasModule` capturado en el closure del useEffect. Las deps
        // del effect están incompletas a propósito (agregar `hasModule`/módulos lo re-suscribe
        // y puede reintroducir el incidente #1004), así que el closure puede quedar STALE cuando
        // los módulos cargan tarde → el auto-print BLE se saltaba para siempre. Replicamos la
        // misma lógica de `useModules.hasModule` (some(m.id === ...)) leyendo el slice fresco,
        // igual que ya hacemos con `getWebBlePrintMode()` y `webBlePrinters`.
        const modules = appStore.getState().subscription?.modulesAndPermissions || [];
        const hasAutoPrintModule = modules.some((m) => m.id === 'IMPRESION_AUTOMATICA');
        if (!hasAutoPrintModule) return;
        if (order.pedido.estatus === 'pre_checkout') return;
        const printers = appStore.getState().webBlePrinters || {};
        // FIX doble impresión (reporte de pruebas): qué impresoras imprimen según el trigger.
        //  - 'received' (pedido normal recién entrado): SOLO las 'all' ("Al recibir").
        //  - 'accepted' de un pedido NORMAL (el cajero tocó Aceptar): SOLO las 'confirmed'
        //    ("Al aceptar"). Las 'all' YA imprimieron en 'received' → incluirlas aquí las
        //    reimprimía (doble impresión: imprimía al recibir Y al aceptar).
        //  - 'accepted' de un pedido TELEFÓNICO: AMBOS sets ('confirmed' + 'all'), porque el
        //    pedido nace aceptado y dispara una sola vez 'accepted' (no hay 'received' previo).
        //    Paridad con Electron, que para teléfono imprime onOrderAccepted + onOrderReceived.
        const wanted = webBleAutoPrintWanted(trigger, order.pedido.origin === 'telefono');
        // DEDUP por firma de output (espeja selectAutoPrintPrinters de Electron / PR #1050): dos
        // perfiles BLE que apuntan a la MISMA impresora física con la misma config colapsan a UNA
        // impresión → evita la doble impresión. Multi-ticket legítimo (caja+cocina) se preserva.
        selectAutoPrintWebBlePrinters(printers, wanted).forEach((p) => {
          webBlePrintOrder({ detailedOrder: order, orderId: order.pedido.id, settings: p });
        });
      } catch {
        /* el auto-print BLE nunca debe romper la recepción de pedidos */
      }
    };

    const onNewOrder = async (incomingOrder: OrderEvent) => {
      try {
        const commerceToken = accountCommerceTokensRef.current?.find(
          (item) => item.id_comercio === incomingOrder.commerceId,
        );
        const order = await getOrder(incomingOrder.orderId);
        // No agregar pedidos programados que aún no se activan al Kanban
        if (order.pedido.estatus === 'scheduled') return;
        if (order.pedido.is_scheduled && order.pedido.auto_fire_at && new Date(order.pedido.auto_fire_at) > new Date())
          return;
        const isCurrentCommerce = isEventForCurrentCommerce(
          incomingOrder.commerceId,
          localStorage.getItem('id_commerce'),
        );
        // Ignorar por completo (sin Kanban, sin toast, sin alarma, sin impresión) los pedidos de
        // otras sucursales cuando el usuario no tiene permiso multisucursal. La suscripción al
        // canal de cuenta se mantiene intacta (es la que entrega también los pedidos propios).
        if (shouldIgnoreIncomingOrder(isCurrentCommerce, canSeeOtherBranchOrdersRef.current)) return;
        if (isCurrentCommerce) handleAddOrder(order.pedido);
        // Si este pedido venía de la cola de programados (auto-fire lo activó), sacarlo del slice
        // `scheduledOrders` para que la pestaña "Programados" y su contador dejen de mostrarlo.
        // No-op si no estaba en la lista.
        appStore.dispatch(scheduledOrdersSlice.actions.removeScheduledOrder({ id: order.pedido.id }));
        // Auto-impresión: gate de negocio (módulo activo + sucursal actual) evaluado UNA vez,
        // combinado con idempotencia por evento (un mismo NEW_ORDER entregado dos veces por el
        // socket no re-imprime). El veredicto se comparte entre el bloque de teléfono
        // (onOrderAccepted) y el de recepción (onOrderReceived) para marcar la dedup una sola vez.
        // Módulo LEÍDO FRESCO del store (no el `hasModule` del closure, que puede quedar stale si
        // los módulos cargan tarde → dejaría muerto el auto-print —Electron Y BLE— para siempre;
        // fix Seer HIGH). Combinado con la idempotencia por evento de PR #1050 (shouldSkipAutoPrint).
        const canAutoPrint =
          shouldAutoPrintIncomingOrder(
            isCurrentCommerce,
            (appStore.getState().subscription?.modulesAndPermissions || []).some(
              (m) => m.id === 'IMPRESION_AUTOMATICA',
            ),
          ) && !shouldSkipAutoPrint(autoPrintedOrdersRef.current, String(order.pedido.id), Date.now());
        if (order.pedido.origin === 'telefono') {
          toast.success(`Pedido ${order.pedido.folio} registrado`);
          trackEvent(TRACK_EVENT_NAMES.MenuOrderPhoneNewOrder);

          navigate('/control-pedidos');
          if (canAutoPrint) {
            // Deduplicado por firma de output (PR #1050): dos entradas a la MISMA impresora física
            // con la misma config salen una sola vez (raíz del bug de doble impresión). El gate
            // canAutoPrint ya lee el módulo FRESCO del store (fix Seer) + idempotencia por evento.
            selectAutoPrintPrinters(allPrintersSettings, PRINT_AUTO_OPTIONS_MAP.onOrderAccepted.key).forEach(
              (settings) => {
                new Array(settings.electronPrintNumber).fill('mock').forEach((_, index) => {
                  setTimeout(
                    () => {
                      printOrder({ detailedOrder: order, orderId: order.pedido.id, settings });
                    },
                    settings.electronMultiPrintDelayBetweenPrints * index * 1000,
                  );
                });
              },
            );
            autoPrintWebBle(order, 'accepted');
          }
        } else {
          const isSelfService = order?.pedido?.self_service?.order_uuid;

          addNotification({
            id: order.pedido.id + NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key,
            title: 'Pedido nuevo',
            subtitle: `No: ${order.pedido.folio}`,
            description: isSelfService
              ? `Mesa: ${order.pedido.self_service?.table_number}`
              : `Cliente: ${order.pedido.nombre}`,
            body: (
              <div className="flex">
                <p className="mr-3">
                  {order.productos.length} producto{order.productos.length !== 1 ? 's' : ''}
                </p>
                <p className="font-semibold text-color-secondary">Total: {formatToCurrency(order.pedido.total)}</p>
              </div>
            ),
            icon: DELIVERY_MAP[order.pedido.metodo_entrega].icon,
            accentColor: DELIVERY_MAP[order.pedido.metodo_entrega].iconColor,
            metodo_entrega: order.pedido.metodo_entrega,
            acceptLabel: isCurrentCommerce ? undefined : 'Ver',
            onAccept: isCurrentCommerce
              ? (cook_time?: number) => {
                  handleUpdateStatus(order.pedido, 'next', true, cook_time);
                  removeNotification(order.pedido.id + NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key, true);

                  if (hasModule('IMPRESION_AUTOMATICA')) {
                    // Acción MANUAL (el usuario aceptó): se deduplica por firma de output pero NO
                    // se aplica la idempotencia por evento (no es una entrega del socket).
                    selectAutoPrintPrinters(allPrintersSettings, PRINT_AUTO_OPTIONS_MAP.onOrderAccepted.key).forEach(
                      (settings) => {
                        new Array(settings.electronPrintNumber).fill('mock').forEach((_, index) => {
                          setTimeout(
                            () => {
                              printOrder({ detailedOrder: order, orderId: order.pedido.id, settings });
                            },
                            settings.electronMultiPrintDelayBetweenPrints * index * 1000,
                          );
                        });
                      },
                    );
                  }
                  autoPrintWebBle(order, 'accepted');
                }
              : () => refreshLogin(commerceToken?.token || '', commerceToken?.id_comercio || ''),
            rejectLabel: 'Ignorar',
            // "Ignorar" debe estar SIEMPRE disponible, también para pedidos de OTRA sucursal.
            // Antes era `undefined` para los ajenos → Notification.tsx solo renderiza el botón con
            // `{onReject && ...}`, así que no aparecía y la alarma en loop no podía silenciarse sin
            // cambiar de sucursal y volver. removeNotification(id, true) detiene el sonido solo si no
            // quedan otras alarmas activas (ver NotificationsContext: silenciado colateral).
            onReject: () => removeNotification(order.pedido.id + NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key, true),
          });

          // Refuerzo: notificación de escritorio del navegador si la pestaña del panel
          // está en SEGUNDO PLANO (solo pedidos de la sucursal actual; los de otra
          // sucursal no deben alertar). Opt-in y a prueba de fallos — el helper jamás
          // lanza, así que nunca rompe la recepción del pedido.
          if (isCurrentCommerce) {
            notifyNewOrder({
              title: 'Pedido nuevo',
              body: isSelfService
                ? `Mesa ${order.pedido.self_service?.table_number} · ${formatToCurrency(order.pedido.total)}`
                : `${order.pedido.nombre} · ${formatToCurrency(order.pedido.total)}`,
            });
          }
        }
        if (canAutoPrint) {
          // Deduplicado por firma de output (PR #1050): si dos entradas del Record apuntan a la
          // misma impresora física con la misma config (causa del bug "regresó la doble impresión"),
          // el ticket sale una sola vez. El multi-ticket legítimo (firmas distintas) se preserva.
          selectAutoPrintPrinters(allPrintersSettings, PRINT_AUTO_OPTIONS_MAP.onOrderReceived.key).forEach(
            (settings) => {
              new Array(settings.electronPrintNumber).fill('mock').forEach((_, index) => {
                setTimeout(
                  () => {
                    // No imprimir automáticamente órdenes en pre_checkout
                    if (order.pedido.estatus !== 'pre_checkout') {
                      printOrder({ detailedOrder: order, orderId: order.pedido.id, settings });
                    }
                  },
                  settings.electronMultiPrintDelayBetweenPrints * index * 1000,
                );
              });
            },
          );
          // Solo para pedidos NO telefónicos: el pedido telefónico ya disparó arriba
          // `autoPrintWebBle(order, 'accepted')`, que para teléfono imprime AMBOS sets
          // ('confirmed' + 'all'). Volver a llamarlo aquí con 'received' imprimiría las impresoras
          // 'all' por segunda vez. Preserva la lógica BLE actual sin tocarla.
          if (order.pedido.origin !== 'telefono') autoPrintWebBle(order, 'received');
        }
      } catch (e) {
        toast.error('Error al obtener un pedido entrante');
      }
    };

    // Pedido programado recién creado (evento `scheduledOrderCreated`). Notificación SILENCIOSA
    // respecto al flujo de aceptar/rechazar: NO entra al Kanban, NO se imprime, NO pide acción.
    // Toast diferenciado ámbar (#f59e0b), auto-cierre 15s, con folio/cliente/total/fecha + CTA
    // "Ver en Programados" (JS-PANEL-002). Reusa el mismo audio de notificación del panel.
    const onScheduledOrder = (incomingOrder: OrderEvent) => {
      try {
        // Mismo patrón que onNewOrder: resolver el token/id_comercio de la sucursal DUEÑA del
        // pedido (puede no ser la activa) para poder cambiar de sesión desde el CTA.
        const commerceToken = accountCommerceTokensRef.current?.find(
          (item) => item.id_comercio === incomingOrder.commerceId,
        );
        const isCurrentCommerce = isEventForCurrentCommerce(
          incomingOrder.commerceId,
          localStorage.getItem('id_commerce'),
        );
        if (shouldIgnoreIncomingOrder(isCurrentCommerce, canSeeOtherBranchOrdersRef.current)) return;

        // Insertar en el slice `scheduledOrders` para que la lista y el contador de la pestaña
        // "Programados" se actualicen en tiempo real (sin refetch). Se trae el pedido completo
        // (mismo patron que onNewOrder) para que la fila renderice con todos sus campos. El slice
        // y el contador son per-sucursal (igual que handleAddOrder en onNewOrder), así que un
        // pedido de OTRA sucursal NO se inyecta aquí — se siembra recién en onAccept, cuando
        // refreshLogin ya cambió el contexto a esa sucursal (ver abajo).
        if (isCurrentCommerce) {
          getOrder(incomingOrder.orderId)
            .then((detail) => {
              if (detail?.pedido) {
                appStore.dispatch(scheduledOrdersSlice.actions.addScheduledOrder(detail.pedido));
              }
            })
            .catch(() => {
              /* si el GET falla, la lista se reconcilia al abrir la pestaña (refetch on mount) */
            });
        }

        const notifId = `${incomingOrder.orderId}_scheduled_created`;
        addNotification({
          id: notifId,
          title: 'Pedido programado',
          subtitle: `No: ${incomingOrder.orderNumber ?? ''}`,
          description: `Cliente: ${incomingOrder.customerName ?? ''}`,
          body: (
            <div className="flex flex-col">
              {incomingOrder.total !== null && incomingOrder.total !== undefined && (
                <p className="font-semibold text-color-secondary">
                  Total: {formatToCurrency(Number(incomingOrder.total))}
                </p>
              )}
              {incomingOrder.scheduled_delivery_time && (
                <p>Programado para: {formatScheduledDateTime(incomingOrder.scheduled_delivery_time)}</p>
              )}
            </div>
          ),
          icon: 'tabler:calendar-clock',
          accentColor: '#f59e0b',
          autoCloseTimer: 15000,
          acceptLabel: 'Ver en Programados',
          onAccept: () => {
            // Multisucursal (espejo de onNewOrder): si el pedido programado es de OTRA sucursal,
            // primero cambiar de sesión a esa sucursal vía refreshLogin. Sin esto el CTA navegaba
            // a la pestaña "Programados" de la sucursal EQUIVOCADA (el fetch y el slice están
            // scoped al comercio activo) y el pedido nunca aparecía para resaltar.
            if (!isCurrentCommerce) {
              refreshLogin(commerceToken?.token || '', commerceToken?.id_comercio || '');
              // Sembrar el slice de la sucursal destino con el pedido para no dejarlo fuera solo
              // porque, al momento del evento, `isCurrentCommerce` era `false` (ese booleano quedó
              // stale: ahora, tras refreshLogin, esta sucursal SÍ es la activa). Complementa —no
              // reemplaza— el refetch on-mount de ScheduledOrdersList tras el remount del router.
              getOrder(incomingOrder.orderId)
                .then((detail) => {
                  if (detail?.pedido) {
                    appStore.dispatch(scheduledOrdersSlice.actions.addScheduledOrder(detail.pedido));
                  }
                })
                .catch(() => {
                  /* si falla, se reconcilia con el refetch on-mount de ScheduledOrdersList */
                });
            }
            // Pasa el orderId para que la lista haga scroll + resalte el pedido recién llegado
            // (la lista se ordena por fecha de entrega, así que el nuevo puede quedar enterrado).
            navigate(`/control-pedidos?tab=scheduled&highlight=${incomingOrder.orderId}`);
            removeNotification(notifId, true);
          },
          rejectLabel: 'Ok',
          onReject: () => removeNotification(notifId, true),
        });
      } catch {
        /* nunca romper la recepción de un pedido programado */
      }
    };

    subscribeEvent(SOCKET_EVENTS.NEW_ORDER, onNewOrder);
    subscribeEvent(SOCKET_EVENTS.SCHEDULED_ORDER_CREATED, onScheduledOrder);

    return () => {
      unsubscribeEvent(SOCKET_EVENTS.NEW_ORDER, onNewOrder);
      unsubscribeEvent(SOCKET_EVENTS.SCHEDULED_ORDER_CREATED, onScheduledOrder);
    };
    // `navigate` (useNavigate v6) es estable entre renders, así que incluirlo no re-suscribe el
    // socket; solo evita el stale-closure al navegar desde el toast de pedido programado.
  }, [handleAddOrder, allPrintersSettings, planId, navigate]);
};
